/**
 * Электронная коммерция Яндекс.Метрики (dataLayer).
 *
 * Вся логика собрана здесь: компоненты не собирают payload руками и не трогают
 * `window.dataLayer` напрямую — они вызывают методы из `useEcommerce()`
 * (см. `./useEcommerce`) или функции ниже.
 *
 * Отличия от прежней (удалённой в 68ee5549) реализации:
 *  - нет обращений вида `sections[id].NAME` — категория резолвится безопасно,
 *    отсутствие разделов больше не роняет страницу;
 *  - события не отправляются менеджерам и админам (как в `reachGoal`);
 *  - любой сбой проглатывается, метрика не может сломать покупку;
 *  - `purchase` отправляется на шаге оформления, как только бэк вернул
 *    ORDER_ID, — заказ в этот момент уже создан.
 *
 * Формат payload — тот же, что понимает счётчик Метрики
 * (`ym(id, "init", { ecommerce: "dataLayer" })`).
 */
import { isAutomatedSession } from "./isAutomated";

const CURRENCY_CODE = "RUB";

/** Значение поля `list` — откуда пользователь взаимодействовал с товаром. */
export const ECOMMERCE_LISTS = {
  catalog: "Каталог",
  detail: "Детальная страница",
  cartPage: "Страница корзины",
  cartPopup: "Всплывающая корзина",
  buyWithThis: "С этим товаром покупают",
  delivery: "Блок доставки",
  electrics: "Подбор электрики",
  install: "Установка",
} as const;

export type EcommerceList =
  (typeof ECOMMERCE_LISTS)[keyof typeof ECOMMERCE_LISTS];

export type EcommerceProduct = {
  id: string | number;
  name?: string;
  price?: number;
  brand?: string;
  category?: string;
  quantity?: number;
  list?: string;
  variant?: string;
};

type Sections = Record<string | number, { NAME?: string }> | any[];

type MapOptions = {
  sections?: Sections;
  list?: string;
  quantity?: number;
};

type BaseOptions = {
  user?: any;
};

/** Метрику не пишем для сотрудников — иначе статистика каталога врёт. */
function isTrackingDisabled(user?: any) {
  return Boolean(user?.IS_MANAGER || user?.IS_ADMIN);
}

function toNumber(value: any): number | undefined {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (typeof num !== "number" || Number.isNaN(num)) {
    return undefined;
  }
  return num;
}

/**
 * Убираем undefined-поля: Метрика не любит пустые значения в товаре.
 * Без Object.fromEntries — его нет в старых браузерах (Chrome < 73),
 * и карточка товара падала в error boundary.
 */
function compact(product: EcommerceProduct): EcommerceProduct {
  const result: any = {};
  Object.keys(product).forEach((key) => {
    const value = (product as any)[key];
    if (value !== undefined) {
      result[key] = value;
    }
  });
  return result as EcommerceProduct;
}

export function getSectionName(
  sections: Sections | undefined,
  sectionId: string | number | undefined | null,
): string | undefined {
  if (!sections || sectionId === undefined || sectionId === null) {
    return undefined;
  }
  return (sections as any)[sectionId as any]?.NAME;
}

/** Товар каталога/детальной (ответ `sections.*`) → товар для Метрики. */
export function toEcommerceProduct(
  product: any,
  { sections, list, quantity }: MapOptions = {},
): EcommerceProduct | null {
  if (!product?.ID) {
    return null;
  }
  return compact({
    id: product.ID,
    name: product.NAME,
    price: toNumber(
      product.CATALOG?.MIN_PRICE ?? product.CATALOG?.PRICE ?? product.PRICE,
    ),
    brand: product.BRAND?.name,
    category: getSectionName(sections, product.IBLOCK_SECTION_ID),
    quantity,
    list,
  });
}

/** Строка корзины (`BasketItem`) → товар для Метрики. */
export function toEcommerceBasketItem(
  item: any,
  { sections, list, quantity }: MapOptions = {},
): EcommerceProduct | null {
  if (!item) {
    return null;
  }
  const id = item.PRODUCT_ID ?? item.ID;
  if (!id) {
    return null;
  }
  return compact({
    id,
    name: item.NAME ?? item.PRODUCT_DATA?.NAME,
    price: toNumber(item.PRICE),
    quantity: quantity ?? item.QUANTITY,
    category: getSectionName(
      sections,
      item.SECTION_ID ?? item.PRODUCT_DATA?.SECTION_ID,
    ),
    list,
  });
}

function pushEcommerce(payload: Record<string, any>, user?: any) {
  if (
    typeof window === "undefined" ||
    isTrackingDisabled(user) ||
    isAutomatedSession()
  ) {
    return;
  }
  try {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      ecommerce: {
        currencyCode: CURRENCY_CODE,
        ...payload,
      },
    });
  } catch (e) {
    console.log("ecommerce push error", e);
  }
}

function normalize(products: any[]): EcommerceProduct[] {
  return products.filter(Boolean) as EcommerceProduct[];
}

/** Показ списка товаров (каталог, подборки). */
export function ecommerceImpressions(
  products: (EcommerceProduct | null)[],
  { user }: BaseOptions = {},
) {
  const impressions = normalize(products);
  if (impressions.length === 0) {
    return;
  }
  pushEcommerce({ impressions }, user);
}

/** Клик по карточке товара в списке. */
export function ecommerceClick(
  product: EcommerceProduct | null,
  { user }: BaseOptions = {},
) {
  if (!product) {
    return;
  }
  pushEcommerce({ click: { products: [product] } }, user);
}

/** Просмотр карточки товара. */
export function ecommerceDetail(
  product: EcommerceProduct | null,
  { user }: BaseOptions = {},
) {
  if (!product) {
    return;
  }
  pushEcommerce({ detail: { products: [product] } }, user);
}

/** Добавление в корзину (в т.ч. увеличение количества). */
export function ecommerceAdd(
  products: (EcommerceProduct | null)[] | EcommerceProduct | null,
  { user }: BaseOptions = {},
) {
  const list = normalize(Array.isArray(products) ? products : [products]);
  if (list.length === 0) {
    return;
  }
  pushEcommerce({ add: { products: list } }, user);
}

/** Удаление из корзины (в т.ч. уменьшение количества). */
export function ecommerceRemove(
  products: (EcommerceProduct | null)[] | EcommerceProduct | null,
  { user }: BaseOptions = {},
) {
  const list = normalize(Array.isArray(products) ? products : [products]);
  if (list.length === 0) {
    return;
  }
  pushEcommerce({ remove: { products: list } }, user);
}

/** Покупка. `orderId` обязателен — по нему Метрика склеивает заказы. */
export function ecommercePurchase(
  {
    orderId,
    total,
    products,
  }: {
    orderId: string | number;
    total?: number;
    products: (EcommerceProduct | null)[];
  },
  { user }: BaseOptions = {},
) {
  const list = normalize(products);
  if (!orderId || list.length === 0) {
    return;
  }
  pushEcommerce(
    {
      purchase: {
        actionField: compact({ id: orderId, revenue: total } as any),
        products: list,
      },
    },
    user,
  );
}
